import React from 'react';
import { Helmet } from 'react-helmet';

import { Flex } from 'src/common/components/flex/flex';
import { PublicPageLayout } from 'src/common/components/public-page-layout/public-page-layout';
import { Text } from 'src/common/components/text/text';
import { withProviders } from 'src/common/hocs/with-providers';

import './Cookies.scss';
import CookieTable from './components/CookieTable';
import { useCookieData } from './hooks/useCookieData';

const CookiesComponent: React.FC = () => {
  const { groupedNonEmptyCookieCategories, isLoading, error } = useCookieData();

  const renderHeader = () => (
    <header className="cookies__header">
      <h1 className="cookies__title" style={{ marginBottom: 24 }}>
        Cookies
      </h1>
      <Text variant="secondary" className="cookies__subtitle">
        Learn about how Streamable uses cookies and similar technologies
      </Text>
    </header>
  );

  const renderMessageState = (message: string) => (
    <Flex
      direction="column"
      alignItems="center"
      justifyContent="flex-start"
      className="cookies__message-container"
    >
      <Text>{message}</Text>
    </Flex>
  );

  const renderContent = () => (
    <div className="cookies__content">
      <section className="cookies__section">
        <h2>What are cookies?</h2>
        <Text as="p">
          When we use the word "cookies", we mean any tracking technology that
          stores or accesses information on the user's device, including any
          SDK, tracking pixel, HTML5 local storage, local shared object, and
          fingerprinting technique.
        </Text>
        <Text as="p">
          If you wish to have more information on cookies and understand your
          rights and how to disable them, visit Section 12 (Cookies) of our
          Privacy Policy.
        </Text>
      </section>

      <section className="cookies__section">
        <h2>What cookies do we use?</h2>
        {groupedNonEmptyCookieCategories.length > 0 ? (
          <>
            <Text as="p">
              We use {groupedNonEmptyCookieCategories.length}{' '}
              {groupedNonEmptyCookieCategories.length === 1 ? 'type' : 'types'}{' '}
              of cookies:{' '}
              {groupedNonEmptyCookieCategories
                .map((cat) => cat.name.toLowerCase())
                .join(', ')}
              .
            </Text>

            {groupedNonEmptyCookieCategories.map((category) => (
              <Text key={category.optanonId} as="p">
                <strong>{category.name}</strong>
                {category.description
                  ? `: ${category.description}`
                  : ': Not available'}
              </Text>
            ))}

            <Text as="p">
              Please see a complete list of cookies in use on streamable.com
              below.
            </Text>

            {groupedNonEmptyCookieCategories.map((category) => (
              <section key={category.optanonId} className="cookies__category">
                <h3>{category.name}</h3>
                <CookieTable
                  cookies={[
                    ...category.cookies,
                    ...(category.children
                      ?.map((child) => child.cookies)
                      .flat() ?? []),
                  ]}
                />
              </section>
            ))}
          </>
        ) : (
          <Text as="p">No cookies are currently in use on streamable.com.</Text>
        )}
      </section>
    </div>
  );

  if (isLoading) {
    return (
      <PublicPageLayout>
        <div className="cookies">
          {renderHeader()}
          {renderMessageState('Loading cookie information...')}
        </div>
      </PublicPageLayout>
    );
  }

  if (error) {
    return (
      <PublicPageLayout>
        <div className="cookies">
          {renderHeader()}
          {renderMessageState(
            'Failed to load cookie information. Please try again later or contact support if the problem persists.'
          )}
        </div>
      </PublicPageLayout>
    );
  }

  return (
    <PublicPageLayout>
      <Helmet>
        <title>{window._STM_PREDATA.cookies.title}</title>
        <meta
          name="description"
          content={window._STM_PREDATA.cookies.description}
        />
        <link rel="canonical" href="https://streamable.com/cookies" />
      </Helmet>

      <div className="cookies">
        {renderHeader()}
        {renderContent()}
      </div>
    </PublicPageLayout>
  );
};

export default withProviders({ design: 'v2', isDarkModeSupported: false })(
  CookiesComponent
);
